import { RegistrationContainer } from '@bumble/app/components/RegistrationContainer';
import { MfaContext } from '@bumble/app/context';
import { useAccount } from '@bumble/app/hooks/use-account';
import { zodResolver } from '@hookform/resolvers/zod';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyIcon from '@mui/icons-material/Key';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { MouseEvent, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TypeOf, object, string } from 'zod';
import FormInput from '../../components/FormInput';
import { useSubscriber } from '../../context/SubscriberContext';

const loginSchema = object({
  username: string().min(1, 'Email address is required').email('Email Address is invalid'),
  password: string()
    .min(1, 'Password is required')
    .min(8, 'Password must be more than 8 characters')
    .max(32, 'Password must be less than 32 characters'),
});

export type LoginInput = TypeOf<typeof loginSchema>;

type LoginObj = LoginInput & {
  subscriber?: string;
  account_id?: string;
};

type AccountOption = { label: string; value: string };

export const Login = () => {
  const mfaContext = useContext(MfaContext);
  if (mfaContext && mfaContext?.user) {
    mfaContext?.setUser(null);
  }
  const { login, isLoggingIn, accountChoices } = useAccount();
  const [showPassword, setShowPassword] = useState(false);
  const [invalidVerifyLink, setInvalidVerifyLink] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAccountId, setSelectedAccountId] = useState<string>('None');
  const subscriberParam = searchParams.get('subscriber');
  const emailVerifiedParam = searchParams.get('email_verified');
  const { subscriber, setSubscriber } = useSubscriber();

  const accountOptions: AccountOption[] = useMemo(
    () =>
      Array.isArray(accountChoices)
        ? accountChoices.map((a) => ({
            label: a.account_domain,
            value: String(a.account_id),
          }))
        : [],
    [accountChoices]
  );

  useEffect(() => {
    if (subscriberParam) {
      setSubscriber(subscriberParam);
    }
  }, [setSubscriber, subscriberParam]);

  // Show a one-time toast after the user clicks the email-verification link,
  // then strip the param so a refresh doesn't repeat it.
  useEffect(() => {
    if (!emailVerifiedParam) return;
    if (emailVerifiedParam === 'true') {
      toast.success(
        'Congratulations. Your account email has been verified. You may proceed to login.'
      );
    } else if (emailVerifiedParam === 'invalid') {
      // Persistent, actionable banner (rendered below) rather than a transient toast.
      setInvalidVerifyLink(true);
    } else {
      toast.error('Something went wrong verifying your email. Please try again.');
    }
    searchParams.delete('email_verified');
    setSearchParams(searchParams, { replace: true });
  }, [emailVerifiedParam, searchParams, setSearchParams]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) =>
    void event.preventDefault();

  const methods = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const { handleSubmit } = methods;

  useEffect(() => {
    setSelectedAccountId((prev) => {
      if (prev && accountOptions.some((o) => o.value === prev)) return prev;
      return 'None';
    });
  }, [accountOptions]);

  const handleAccountChange = (e: SelectChangeEvent) => {
    setSelectedAccountId(e.target.value);
  };

  const submitHandler: SubmitHandler<LoginObj> = (values) => {
    const loginObj = { ...values };
    if (subscriber) {
      loginObj.subscriber = subscriber;
    }
    if (selectedAccountId !== 'None') {
      loginObj.account_id = selectedAccountId;
    }
    login(loginObj);
  };

  return (
    <RegistrationContainer>
      <Typography variant="body1" component="p" py={2} mb={2}>
        Welcome back! Please enter your details.
      </Typography>
      {invalidVerifyLink && (
        <Alert severity="warning" sx={{ textAlign: 'left', mb: 3 }}>
          This email verification link is invalid or has expired.{' '}
          <Link to="/resend-verification">Resend verification email</Link>
        </Alert>
      )}
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(submitHandler)} noValidate autoComplete="off">
          <FormInput
            id="username"
            name="username"
            label="Email Address"
            type="text"
            startAdornment={
              <InputAdornment position="start">
                <EmailOutlinedIcon />
              </InputAdornment>
            }
          />
          <FormInput
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            startAdornment={
              <InputAdornment position="start">
                <KeyIcon />
              </InputAdornment>
            }
          />
          {Array.isArray(accountOptions) && accountOptions.length > 0 && <Box mt={2}>
            <FormControl fullWidth>
              <InputLabel id="account-select-label">Account</InputLabel>
              <Select
                labelId="account-select-label"
                label="Account"
                value={selectedAccountId}
                onChange={handleAccountChange}
              >
                <MenuItem value='None'>None</MenuItem>
                {accountOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>}
          <Stack direction="row" justifyContent="space-between" alignItems="center" my={2}>
            <Box />
            <Button
              size="small"
              variant="text"
              color="secondary"
              component={Link}
              to="/forgot-password"
            >
              Forgot password?
            </Button>
          </Stack>
          <Box mt={4}>
            <Button variant="contained" size="large" fullWidth type="submit" disabled={isLoggingIn}>
              Login
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <Box py={6}>
        <Divider />
      </Box>
      <Button size="small" variant="text" component={Link} to="/register">
        Don&apos;t have an account?
      </Button>
    </RegistrationContainer>
  );
};
